export class CryptoWorkerClient {
  readonly #worker = new Worker(new URL("./crypto-worker.ts", import.meta.url), { type: "module" });
  readonly #pending = new Map<number, { resolve(value: unknown): void; reject(error: Error): void }>();
  #sequence = 0;
  #destroyed = false;

  constructor() {
    this.#worker.onmessage = (event: MessageEvent<{ id: number; ok: boolean; value?: unknown; error?: string }>) => {
      const pending = this.#pending.get(event.data.id);
      if (!pending) return;
      this.#pending.delete(event.data.id);
      if (event.data.ok) pending.resolve(event.data.value);
      else pending.reject(new Error(event.data.error ?? "Crypto worker operation failed"));
    };
  }

  initialize(key: string, keyId: string, keyEpoch: number) {
    return this.call("initialize", { key, keyId, keyEpoch });
  }

  call<T>(operation: string, payload: Record<string, unknown>): Promise<T> {
    if (this.#destroyed) return Promise.reject(new Error("Crypto worker closed"));
    const id = ++this.#sequence;
    return new Promise((resolve, reject) => {
      this.#pending.set(id, { resolve: resolve as (value: unknown) => void, reject });
      this.#worker.postMessage({ id, operation, payload });
    });
  }

  destroy(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    for (const pending of this.#pending.values()) pending.reject(new Error("Crypto worker closed"));
    this.#pending.clear();
    // Terminating the isolated worker releases its key material immediately.
    // Do not enqueue a tracked `clear` request and then reject that same request.
    this.#worker.terminate();
  }
}
